import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Space,
  Button,
  Drawer,
  Radio,
  Input,
  Tag,
  Form
} from '@lockerpm/design';

import common from '../../../../utils/common';
import global from '../../../../config/global';
import relayServices from '../../../../services/relay';

function AliasForm(props) {
  const {
    visible = false,
    item = null,
    isEdit = false,
    onClose = () => {},
  } = props
  const { t } = useTranslation();

  const plan = useSelector((state) => state.payment.plan);
  const userInfo = useSelector(state => state.auth.userInfo);

  const [callingAPI, setCallingAPI] = useState(false);
  const [aliasStatus, setAliasStatus] = useState('forward');

  const [form] = Form.useForm();
  const aliasAddress = Form.useWatch('aliasAddress', form) || item?.address

  useEffect(() => {
    if (item) {
      form.setFieldsValue({
        aliasAddress: item.address
      })
      setAliasStatus(common.aliasStatus(item))
    }
  }, [visible, item])

  const isFreePlan = useMemo(() => {
    return common.isFreePlan();
  }, [plan])

  const isEnterpriseMember = useMemo(() => {
    return common.isEnterpriseMember();
  }, [userInfo])

  const payload = useMemo(() => {
    const data = {
      address: aliasAddress || item?.address,
      block_spam: false,
      enabled: true,
    }
    if (aliasStatus === 'block_spam') {
      data.block_spam = true;
    } else if (aliasStatus === 'block_all_emails') {
      data.block_spam = true;
      data.enabled = false;
    }
    return data
  }, [aliasStatus, aliasAddress])

  const updateAlias = async () => {
    form.validateFields().then(async () => {
      setCallingAPI(true);
      await relayServices.update_address(item.id, payload).then(() => {
        global.pushSuccess(t('notification.success.alias.updated'));
        onClose(true)
      }).catch((error) => {
        global.pushError(error)
      })
      setCallingAPI(false);
    })
  }

  return (
    <div className={props.className}>
      <Drawer
        title={t( `private_email.manage_alias.title`)}
        placement="right"
        width={500}
        onClose={onClose}
        open={visible}
        footer={
          <Space className='flex items-center justify-end'>
            <Button
              disabled={callingAPI}
              onClick={onClose}
            >
              {t('button.cancel')}
            </Button>
            {
              (isEdit || !isFreePlan || isEnterpriseMember) && <Button
                type="primary"
                disabled={!aliasAddress}
                loading={callingAPI}
                onClick={updateAlias}
              >
                { t('button.save') } 
              </Button>
            }
          </Space>
        }
      >
        {
          isEdit && <Form
            form={form}
            layout="vertical"
            className='mb-6'
          >
            <Form.Item
              name={'aliasAddress'}
              rules={[
                global.rules.LIMIT_LENGTH(6, 63, t('private_email.manage_alias.alias_address.limit')),
                global.rules.INVALID('', 'ALIAS', t('private_email.manage_alias.alias_address.invalid'))
              ]}
            >
              <Input
                addonAfter={`@${item.subdomain ? `${item.subdomain}.` : ''}${item.domain}`}
                showCount
                minLength={6}
                maxLength={63}
              />
            </Form.Item>
          </Form>
        }
        <div className='flex items-center mb-4'>
          <p className='font-semibold'>{t('private_email.manage_alias.configuration')}</p>
          <Tag className="ml-2 text-sm px-2 pt-[1px] pb-[2px]" color="#3f4254">
            Premium
          </Tag>
        </div>
        <Radio.Group
          onChange={(e) => setAliasStatus(e.target.value)}
          value={aliasStatus}
          disabled={isFreePlan && !isEnterpriseMember}
        >
          <Space direction="vertical" size={16}>
            <Radio value={'forward'}>
              <p className='font-semibold'>{t('private_email.manage_alias.forward')}</p>
              <p className='text-sm'>{t('private_email.manage_alias.forward_desc')}</p>
            </Radio>
            <Radio value={'block_spam'}>
              <p className='font-semibold'>{t('private_email.manage_alias.block_spam')}</p>
              <p className='text-sm'>{t('private_email.manage_alias.block_spam_desc')}</p>
            </Radio>
            <Radio value={'block_all_emails'}>
              <p className='font-semibold'>{t('private_email.manage_alias.block_all_emails')}</p>
              <p className='text-sm'>{t('private_email.manage_alias.block_all_emails_desc')}</p>
            </Radio>
          </Space>
        </Radio.Group>
      </Drawer>
    </div>
  );
}

export default AliasForm;
